import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import { resolveHoverActive } from "@/features/hoverIdle/input/resolveHoverActive";
import {
  createUpgradeHoverRepeater,
  type UpgradeHoverRepeater,
} from "@/features/hoverIdle/hover/upgradeHoverRepeater";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import {
  HOV_AMPLIFIER_UPGRADE_REPEAT_DECAY,
  HOV_AMPLIFIER_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  HOV_AMPLIFIER_UPGRADE_REPEAT_MIN_INTERVAL_MS,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import type { AmplifierUpgradeGuardMs } from "@/features/hoverIdle/types";
import type { HovAmplifierEntrySnapshot } from "./hovAmplifierModel";
import {
  computeAmplifierSnapshotUpgradeState,
  type AmplifierSnapshotUpgradeState,
} from "./hovAmplifierUpgradeState";

export interface HovAmplifierUpgradeInteractionControllerOptions {
  readonly tooltipAnchor: HTMLElement;
  readonly purchaseButton: HTMLButtonElement;
  readonly tooltipRoot: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly getEntrySnapshot: () => HovAmplifierEntrySnapshot | null;
  readonly onPurchase: (entry: HovAmplifierEntrySnapshot) => boolean;
  readonly getGuardMs: () => AmplifierUpgradeGuardMs;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly minIntervalMs?: number;
  readonly decay?: number;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
}

export interface HovAmplifierUpgradeInteractionController extends Disposable {
  clearHover(): void;
  hideTooltip(): void;
  isTooltipShown(): boolean;
  refresh(): void;
  setInputLocked(locked: boolean): void;
}

export function createHovAmplifierUpgradeInteractionController(
  options: HovAmplifierUpgradeInteractionControllerOptions,
): HovAmplifierUpgradeInteractionController {
  return new HovAmplifierUpgradeInteractionControllerImpl(options);
}

class HovAmplifierUpgradeInteractionControllerImpl implements HovAmplifierUpgradeInteractionController {
  private readonly tooltipAnchor: HTMLElement;
  private readonly purchaseButton: HTMLButtonElement;
  private readonly tooltipRoot: HTMLElement;
  private readonly pointerModel: HoverPointerModel;
  private readonly getEntrySnapshot: () => HovAmplifierEntrySnapshot | null;
  private readonly onPurchase: (entry: HovAmplifierEntrySnapshot) => boolean;
  private readonly getGuardMs: () => AmplifierUpgradeGuardMs;
  private readonly repeater: UpgradeHoverRepeater;
  private readonly unsubscribePointer: () => void;
  private guardTimer: ReturnType<typeof setTimeout> | null = null;
  private pulseTimer: ReturnType<typeof setTimeout> | null = null;
  private isTooltipAnchorNativeHovered = false;
  private isTooltipAnchorFocused = false;
  private isPurchasePointerHovering = false;
  private isPurchaseNativeHovering = false;
  private isPurchaseFocused = false;
  private isInputLocked = false;
  private isGuardPending = false;
  private isInitialPurchaseInProgress = false;
  private isRepeaterEngaged = false;
  private shouldSkipNextRepeaterFire = false;
  private isTooltipVisible = false;

  private readonly handleTooltipAnchorPointerEnter = (): void => {
    this.isTooltipAnchorNativeHovered = true;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorPointerLeave = (): void => {
    this.isTooltipAnchorNativeHovered = false;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorFocus = (): void => {
    this.isTooltipAnchorFocused = true;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorBlur = (): void => {
    this.isTooltipAnchorFocused = false;
    this.syncTooltipVisibility();
  };

  private readonly handlePurchaseFocus = (): void => {
    this.isPurchaseFocused = true;
    this.syncPurchaseSession();
  };

  private readonly handlePurchaseBlur = (): void => {
    this.isPurchaseFocused = false;
    this.syncPurchaseSession();
  };

  private readonly handlePurchasePointerEnter = (): void => {
    this.isPurchaseNativeHovering = true;
    this.syncPurchaseSession();
  };

  private readonly handlePurchasePointerLeave = (): void => {
    this.isPurchaseNativeHovering = false;
    this.syncPurchaseSession();
  };

  constructor(options: HovAmplifierUpgradeInteractionControllerOptions) {
    this.tooltipAnchor = options.tooltipAnchor;
    this.purchaseButton = options.purchaseButton;
    this.tooltipRoot = options.tooltipRoot;
    this.pointerModel = options.pointerModel;
    this.getEntrySnapshot = options.getEntrySnapshot;
    this.onPurchase = options.onPurchase;
    this.getGuardMs = options.getGuardMs;
    this.repeater = createUpgradeHoverRepeater({
      initialIntervalMs:
        options.initialIntervalMs ??
        HOV_AMPLIFIER_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
      minIntervalMs:
        options.minIntervalMs ?? HOV_AMPLIFIER_UPGRADE_REPEAT_MIN_INTERVAL_MS,
      decay: options.decay ?? HOV_AMPLIFIER_UPGRADE_REPEAT_DECAY,
      speedMultiplier: options.speedMultiplier,
      onFire: ({ nextIntervalMs }) => this.handleRepeaterFire(nextIntervalMs),
    });

    this.unsubscribePointer = this.pointerModel.subscribe(() => {
      this.refreshPurchaseHover();
    });
    this.refreshPurchaseHover();

    this.tooltipAnchor.addEventListener(
      "pointerenter",
      this.handleTooltipAnchorPointerEnter,
    );
    this.tooltipAnchor.addEventListener(
      "pointerleave",
      this.handleTooltipAnchorPointerLeave,
    );
    this.tooltipAnchor.addEventListener("focus", this.handleTooltipAnchorFocus);
    this.tooltipAnchor.addEventListener("blur", this.handleTooltipAnchorBlur);
    this.purchaseButton.addEventListener(
      "pointerenter",
      this.handlePurchasePointerEnter,
    );
    this.purchaseButton.addEventListener(
      "pointerleave",
      this.handlePurchasePointerLeave,
    );
    this.purchaseButton.addEventListener("focus", this.handlePurchaseFocus);
    this.purchaseButton.addEventListener("blur", this.handlePurchaseBlur);

    this.syncTooltipVisibility();
    this.syncUpgradeStateDataset();
  }

  clearHover(): void {
    this.isTooltipAnchorNativeHovered = false;
    this.isTooltipAnchorFocused = false;
    this.isPurchasePointerHovering = false;
    this.isPurchaseNativeHovering = false;
    this.isPurchaseFocused = false;
    this.syncTooltipVisibility();
    this.stopPurchaseSession();
  }

  dispose(): void {
    this.clearHover();
    this.unsubscribePointer();
    this.repeater.dispose();
    this.tooltipAnchor.removeEventListener(
      "pointerenter",
      this.handleTooltipAnchorPointerEnter,
    );
    this.tooltipAnchor.removeEventListener(
      "pointerleave",
      this.handleTooltipAnchorPointerLeave,
    );
    this.tooltipAnchor.removeEventListener(
      "focus",
      this.handleTooltipAnchorFocus,
    );
    this.tooltipAnchor.removeEventListener(
      "blur",
      this.handleTooltipAnchorBlur,
    );
    this.purchaseButton.removeEventListener("focus", this.handlePurchaseFocus);
    this.purchaseButton.removeEventListener("blur", this.handlePurchaseBlur);
    this.purchaseButton.removeEventListener(
      "pointerenter",
      this.handlePurchasePointerEnter,
    );
    this.purchaseButton.removeEventListener(
      "pointerleave",
      this.handlePurchasePointerLeave,
    );
  }

  hideTooltip(): void {
    this.isTooltipAnchorNativeHovered = false;
    this.isTooltipAnchorFocused = false;
    this.syncTooltipVisibility();
  }

  isTooltipShown(): boolean {
    return this.isTooltipVisible;
  }

  refresh(): void {
    this.refreshPurchaseHover();
    this.syncPurchaseSession();
    this.syncUpgradeStateDataset();
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.isInputLocked) {
      return;
    }
    this.isInputLocked = locked;
    if (locked) {
      this.hideTooltip();
      this.stopPurchaseSession();
      return;
    }
    this.refreshPurchaseHover();
    this.syncPurchaseSession();
  }

  private refreshPurchaseHover(): void {
    const snapshot = this.pointerModel.getSnapshot();
    const rect = this.purchaseButton.getBoundingClientRect();
    const next = this.isButtonBlocked()
      ? false
      : resolveHoverActive(snapshot, rect);
    if (next === this.isPurchasePointerHovering) {
      return;
    }
    this.isPurchasePointerHovering = next;
    this.syncPurchaseSession();
  }

  private syncPurchaseSession(): void {
    const wantsSession =
      !this.isButtonBlocked() && this.shouldContinuePurchaseSession();
    const shouldRun = wantsSession && this.canAttemptPurchase();
    if (shouldRun) {
      this.startPurchaseSession();
    } else {
      this.stopPurchaseSession({ clearPulse: false });
    }
  }

  private startPurchaseSession(): void {
    if (this.isSessionActive()) {
      return;
    }
    const guardMs = this.getGuardMs();
    if (guardMs <= 0) {
      this.purchaseInitialThenStartRepeater();
      return;
    }
    this.isGuardPending = true;
    this.purchaseButton.dataset.amplifierUpgradeSession = "guard";
    this.purchaseButton.style.setProperty(
      "--upgrade-glass-guard-duration",
      `${guardMs}ms`,
    );
    this.syncUpgradeStateDataset();
    this.guardTimer = setTimeout(() => {
      this.guardTimer = null;
      this.isGuardPending = false;
      if (this.canContinuePurchaseSession()) {
        this.purchaseInitialThenStartRepeater();
      } else {
        delete this.purchaseButton.dataset.amplifierUpgradeSession;
      }
      this.syncUpgradeStateDataset();
    }, guardMs);
  }

  private purchaseInitialThenStartRepeater(): void {
    if (this.isSessionActive()) {
      return;
    }
    this.isInitialPurchaseInProgress = true;
    this.purchaseButton.dataset.amplifierUpgradeSession = "purchase";
    const purchased = this.purchaseOnce(this.repeater.getInitialIntervalMs());
    this.isInitialPurchaseInProgress = false;

    if (!purchased) {
      this.stopPurchaseSession();
      return;
    }

    if (this.canContinuePurchaseSession()) {
      this.startRepeater();
    } else {
      this.stopPurchaseSession({ clearPulse: false });
    }
  }

  private startRepeater(): void {
    if (this.isRepeaterEngaged) {
      return;
    }
    this.isRepeaterEngaged = true;
    this.purchaseButton.dataset.amplifierUpgradeSession = "repeat";
    this.shouldSkipNextRepeaterFire = true;
    this.repeater.setEngaged(true);
  }

  private stopPurchaseSession(
    options: { readonly clearPulse?: boolean } = {},
  ): void {
    if (this.guardTimer !== null) {
      clearTimeout(this.guardTimer);
      this.guardTimer = null;
    }
    const wasGuardPending = this.isGuardPending;
    this.isGuardPending = false;
    this.isInitialPurchaseInProgress = false;
    this.shouldSkipNextRepeaterFire = false;
    if (this.isRepeaterEngaged) {
      this.repeater.setEngaged(false);
      this.isRepeaterEngaged = false;
    }
    delete this.purchaseButton.dataset.amplifierUpgradeSession;
    if (wasGuardPending) {
      this.syncUpgradeStateDataset();
    }
    if (options.clearPulse ?? true) {
      this.clearPulse();
    }
  }

  private syncUpgradeStateDataset(): void {
    const state: AmplifierSnapshotUpgradeState | "guarded" = this.isGuardPending
      ? "guarded"
      : computeAmplifierSnapshotUpgradeState(this.getEntrySnapshot());
    if (this.purchaseButton.dataset.upgradeState !== state) {
      this.purchaseButton.dataset.upgradeState = state;
    }
  }

  private shouldContinuePurchaseSession(): boolean {
    return (
      this.isPurchasePointerHovering ||
      this.isPurchaseNativeHovering ||
      this.isPurchaseFocused
    );
  }

  private canContinuePurchaseSession(): boolean {
    return (
      !this.isButtonBlocked() &&
      this.shouldContinuePurchaseSession() &&
      this.canAttemptPurchase()
    );
  }

  private canAttemptPurchase(): boolean {
    const entry = this.getEntrySnapshot();
    return entry !== null && entry.canPurchaseUpgrade;
  }

  private isSessionActive(): boolean {
    return (
      this.isGuardPending ||
      this.isInitialPurchaseInProgress ||
      this.isRepeaterEngaged
    );
  }

  private handleRepeaterFire(nextIntervalMs: number): boolean {
    if (this.shouldSkipNextRepeaterFire) {
      this.shouldSkipNextRepeaterFire = false;
      return true;
    }
    const purchased = this.purchaseOnce(nextIntervalMs);
    if (!purchased) {
      this.stopPurchaseSession();
    }
    return purchased;
  }

  private purchaseOnce(nextIntervalMs: number): boolean {
    if (this.isButtonBlocked()) {
      return false;
    }
    const entry = this.getEntrySnapshot();
    if (entry === null || !entry.canPurchaseUpgrade) {
      return false;
    }
    const purchased = this.onPurchase(entry);
    if (purchased) {
      this.triggerPulse(nextIntervalMs / 1000);
    }
    return purchased;
  }

  private triggerPulse(pulseSeconds: number): void {
    this.clearPulse();
    this.purchaseButton.style.setProperty(
      "--upgrade-pulse-duration",
      `${pulseSeconds.toFixed(3)}s`,
    );
    void this.purchaseButton.offsetWidth;
    this.purchaseButton.dataset.pulse = "true";
    this.pulseTimer = setTimeout(
      () => {
        this.clearPulse();
      },
      Math.max(0, pulseSeconds * 1000),
    );
  }

  private clearPulse(): void {
    if (this.pulseTimer !== null) {
      clearTimeout(this.pulseTimer);
      this.pulseTimer = null;
    }
    delete this.purchaseButton.dataset.pulse;
  }

  private isButtonBlocked(): boolean {
    return (
      this.isInputLocked || this.purchaseButton.closest("[inert]") !== null
    );
  }

  private syncTooltipVisibility(): void {
    const isVisible =
      this.isTooltipAnchorNativeHovered || this.isTooltipAnchorFocused;
    this.isTooltipVisible = isVisible;
    this.tooltipRoot.hidden = !isVisible;
    this.tooltipRoot.setAttribute("aria-hidden", isVisible ? "false" : "true");
  }
}
